import { Target, Eye, Handshake, Users } from "lucide-react";
import { motion } from "framer-motion";
import { PageMasthead } from "@/components/ui-custom/PageMasthead";
import { RevealOnScroll, RevealStagger, staggerItem } from "@/components/ui-custom/RevealOnScroll";
import { SectionHeading } from "@/components/ui-custom/SectionHeading";

export default function Tentang() {
  return (
    <div data-testid="tentang-page">
      <PageMasthead breadcrumb="TENTANG" title="Tentang Kami" subtitle="Satu Komuniti. Satu Standard. Satu Profesion." />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 space-y-20">
        <RevealOnScroll>
          <SectionHeading number="01" title="Siapa Kami" />
          <p className="text-piks-muted leading-relaxed max-w-3xl text-base sm:text-lg">
            Persatuan Pengawal Peribadi Sarawak (PPPS) merupakan sebuah organisasi yang ditubuhkan pada 5 April 2015
            dengan fokus kepada pembangunan profesionalisme dalam bidang pengawal peribadi, perlindungan individu,
            keselamatan serta latihan berkaitan kemahiran perlindungan. Berdaftar rasmi di bawah Akta Pertubuhan 1966
            (No. Pendaftaran: PPM-009-13-05032015).
          </p>
        </RevealOnScroll>

        <RevealOnScroll>
          <SectionHeading number="02" title="Kenapa Bodyguard Sarawak Ditubuhkan" />
          <p className="text-piks-muted leading-relaxed max-w-3xl text-base sm:text-lg">
            PPPS ditubuhkan sebagai sebuah platform yang menyatukan para pengawal peribadi dan pengamal keselamatan
            profesional di Sarawak dalam usaha memperkukuh imej, standard profesionalisme serta pembangunan industri
            pengawalan peribadi — membina satu suara industri yang dipercayai oleh kerajaan, korporat dan orang awam.
          </p>
        </RevealOnScroll>

        <RevealStagger className="grid sm:grid-cols-3 gap-6" staggerDelay={0.1}>
          <motion.div variants={staggerItem}>
            <SectionCard icon={Eye} label="Visi" text="Menjadi organisasi rujukan dalam pembangunan profesionalisme dan standard pengawal peribadi di Sarawak serta menyumbang kepada peningkatan tahap keselamatan dan perlindungan profesional." />
          </motion.div>
          <motion.div variants={staggerItem}>
            <SectionCard icon={Target} label="Misi" text="Meningkatkan profesionalisme pengawal peribadi, menyediakan latihan yang relevan, membentuk anggota yang berdisiplin dan berintegriti, serta membina jaringan kerjasama dengan agensi dan pihak berkepentingan." />
          </motion.div>
          <motion.div variants={staggerItem}>
            <SectionCard icon={Handshake} label="Nilai" text="Profesionalisme, Disiplin, Integriti dan Kesiapsiagaan — nilai yang membentuk setiap tugasan dan keputusan persatuan." />
          </motion.div>
        </RevealStagger>

        <RevealOnScroll>
          <SectionHeading number="03" title="Peranan Persatuan" />
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
            {[
              "Memperkukuh standard profesional & etika pengawal peribadi",
              "Menyediakan latihan & pembangunan kompetensi berterusan",
              "Membina jaringan kerjasama dengan agensi kerajaan & swasta",
              "Mengesahkan identiti & status keahlian secara telus kepada awam",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3 p-4 rounded-xl bg-piks-soft-neutral border border-piks-border">
                <Users className="w-4 h-4 text-piks-red mt-0.5 shrink-0" />
                <p className="text-sm text-piks-muted leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </RevealOnScroll>

        <RevealOnScroll className="relative pl-6 border-l-2 border-piks-gold/50">
          <SectionHeading number="04" title="Sejarah & Pencapaian" />
          <p className="text-piks-muted leading-relaxed max-w-3xl">
            Sejak penubuhannya pada <strong className="text-piks-black">2015</strong>, PPPS telah melaksanakan
            pelbagai aktiviti latihan, pembangunan kompetensi, kesedaran risiko dan penglibatan bersama agensi
            kerajaan dan swasta — termasuk melatih lebih <strong className="text-piks-black">890 peserta</strong>{" "}
            menerusi <strong className="text-piks-black">38 kursus</strong> sepanjang perjalanan persatuan hingga 2026.
          </p>
        </RevealOnScroll>

        <div className="gold-divider" />

        <RevealOnScroll className="grid sm:grid-cols-[220px_1fr] gap-8 sm:gap-10 items-start bg-piks-soft-neutral rounded-2xl border border-piks-border p-6 sm:p-8">
          <div className="flex flex-col items-center sm:items-start">
            <img
              src="/team/president.jpg"
              alt="Cr. Bolhasan Bin Haji Putit"
              className="w-40 h-40 sm:w-full sm:h-auto rounded-xl object-cover border border-piks-border"
              data-testid="president-photo"
            />
            <p className="font-semibold text-piks-black text-sm mt-3 text-center sm:text-left">Cr. Bolhasan Bin Haji Putit</p>
            <p className="text-xs text-piks-red font-medium tracking-wide text-center sm:text-left">PRESIDEN</p>
          </div>
          <div>
            <p className="section-eyebrow mb-3">PERUTUSAN PRESIDEN</p>
            <blockquote className="text-piks-black text-lg sm:text-xl font-display font-extrabold leading-snug mb-4">
              &ldquo;Professionalism. Integrity. Protection.&rdquo;
            </blockquote>
            <p className="text-sm text-piks-muted leading-relaxed mb-3">
              Persatuan Pengawal Peribadi Sarawak ditubuhkan sebagai sebuah platform yang menyatukan para pengawal
              peribadi dan pengamal keselamatan profesional di Sarawak dalam usaha memperkukuh imej, standard
              profesionalisme serta pembangunan industri pengawalan peribadi.
            </p>
            <p className="text-sm text-piks-muted leading-relaxed">
              Dengan semangat bersatu, profesional dan berintegriti, PPPS akan terus berusaha memperkasa ahli serta
              membawa profesion pengawalan peribadi ke tahap yang lebih tinggi, selaras dengan keperluan dan
              cabaran keselamatan masa kini.
            </p>
          </div>
        </RevealOnScroll>
      </section>
    </div>
  );
}

function SectionCard({ icon: Icon, label, text }) {
  return (
    <div className="p-7 rounded-xl bg-white border border-piks-border h-full hover:border-piks-gold/50 hover:-translate-y-1 transition-all duration-300">
      <Icon className="w-6 h-6 text-piks-red mb-4" />
      <h3 className="font-semibold text-piks-black mb-2">{label}</h3>
      <p className="text-sm text-piks-muted leading-relaxed">{text}</p>
    </div>
  );
}
